import React from 'react';
import { User, Mail, Calendar, Shield, Phone, Clock, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { NameVerificationSection } from '../../components/profile/NameVerificationSection';
import type { User as UserProfile } from '../../types';

interface ProfileTabProps {
  user: UserProfile;
  onNameSubmit: (data: any) => Promise<void>;
  onIdUpload: (file: File) => Promise<void>;
}

export const ProfileTab: React.FC<ProfileTabProps> = ({ 
  user, 
  onNameSubmit, 
  onIdUpload 
}) => {
  const getVerificationBadge = () => {
    if (user.is_id_verified) {
      return {
        icon: CheckCircle,
        text: 'Identity Verified',
        bg: 'bg-green-50',
        textColor: 'text-green-700',
        border: 'border-green-200'
      };
    }
    if (user.id_verification_status === 'pending') {
      return {
        icon: Clock,
        text: 'Verification Pending',
        bg: 'bg-yellow-50',
        textColor: 'text-yellow-700',
        border: 'border-yellow-200'
      };
    }
    if (user.id_verification_status === 'rejected') {
      return {
        icon: XCircle,
        text: 'Verification Rejected',
        bg: 'bg-red-50',
        textColor: 'text-red-700',
        border: 'border-red-200'
      };
    }
    return {
      icon: AlertCircle,
      text: 'Not Verified',
      bg: 'bg-gray-50',
      textColor: 'text-gray-600',
      border: 'border-gray-200'
    };
  };

  const badge = getVerificationBadge();
  const BadgeIcon = badge.icon;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header with subtle gradient */}
      <div className="bg-gradient-to-r from-gray-50 to-white px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <User className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
              <p className="text-sm text-gray-500">Manage your personal details and verification status</p>
            </div>
          </div>
          
          {/* Verification Badge */}
          <div className={`flex items-center px-3 py-1.5 rounded-full border ${badge.bg} ${badge.border}`}>
            <BadgeIcon className={`h-4 w-4 ${badge.textColor} mr-1.5`} />
            <span className={`text-xs font-medium ${badge.textColor}`}>{badge.text}</span>
          </div>
        </div>
      </div>

      {/* Profile details */}
      <div className="p-6">
        {/* Name and basic info card */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">Account Overview</h3>
          </div>
          <div className="flex items-center">
            <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center mr-4">
              <span className="text-lg font-semibold text-primary-700">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </span>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">
                {user.first_name} {user.last_name}
              </h3>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Personal Info Column */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact Information</h4>
            
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                <Mail className="h-5 w-5 text-gray-400 mr-3" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">Email Address</div>
                  <div className="font-medium text-gray-900">{user.email}</div>
                </div>
              </div>

              {user.phone && (
                <div className="flex items-center p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">Phone Number</div>
                    <div className="font-medium text-gray-900">{user.phone}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Account Info Column */}
          <div className="space-y-4">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Details</h4>
            
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                <Calendar className="h-5 w-5 text-gray-400 mr-3" />
                <div className="flex-1">
                  <div className="text-xs text-gray-500">Member Since</div>
                  <div className="font-medium text-gray-900">
                    {user.created_at ? new Date(user.created_at).toLocaleDateString(undefined, { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) : 'N/A'}
                  </div>
                </div>
              </div>

              {user.last_login && (
                <div className="flex items-center p-3 bg-white rounded-lg border border-gray-100 hover:border-gray-200 transition-colors">
                  <Clock className="h-5 w-5 text-gray-400 mr-3" />
                  <div className="flex-1">
                    <div className="text-xs text-gray-500">Last Login</div>
                    <div className="font-medium text-gray-900">
                      {new Date(user.last_login).toLocaleString(undefined, {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Verification Section with proper spacing */}
        <div className="mt-8 pt-6 border-t border-gray-100">
          <NameVerificationSection
            user={user}
            onNameSubmit={onNameSubmit}
            onIdUpload={onIdUpload}
          />
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;